import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { PageContainer } from "../../components/v2/PageContainer";
import { FAQFooter } from "../../components/v3/faq/FAQFooter";
import { FAQNoResults } from "../../components/v3/faq/FAQNoResults";
import { FAQCourseDropdown } from "../../components/v3/faq/FAQCourseDropdown";
import {
  getCourseAccessibleLabel,
  spellCategoryLabelForScreenReader,
} from "../../components/v3/faq/faqCourseA11y";
import { FAQTruncatedText } from "../../components/v3/faq/FAQTruncatedText";
import { FAQSearch } from "../../components/v3/faq/FAQSearch";
import { FAQSection } from "../../components/v3/faq/FAQSection";
import useStorageState from "../../hooks/useStorageState";
import BGLeft from "../../assets/Login-BgLeft.svg";
import BGRight from "../../assets/Login-BgRight.svg";
import { ReactComponent as FaqCourseIconAll } from "../../assets/FaqCourseIconAll.svg";
import { ReactComponent as FaqCourseIconAllSelected } from "../../assets/FaqCourseIconAllSelected.svg";
import { ReactComponent as FaqCourseIconBio } from "../../assets/FaqCourseIconBio.svg";
import { ReactComponent as FaqCourseIconBioSelected } from "../../assets/FaqCourseIconBioSelected.svg";
import { ReactComponent as FaqCourseIconChem } from "../../assets/FaqCourseIconChem.svg";
import { ReactComponent as FaqCourseIconChemSelected } from "../../assets/FaqCourseIconChemSelected.svg";
import { ReactComponent as FaqCourseIconCs } from "../../assets/FaqCourseIconCs.svg";
import { ReactComponent as FaqCourseIconCsSelected } from "../../assets/FaqCourseIconCsSelected.svg";
import { FAQCourseTag as CourseTag } from "@backend-types";
import { Course } from "../../components/utils/CourseTree";
import { SimpleCarousel } from "../../components/v3/SimpleCarousel";
import { getSupportLink } from "../../Helpers";

export interface ISection {
  id: string;
  headingText: string;
  headingIcon: React.ReactNode;
  questions: IQuestion[];
}

export interface ICourseTagDetails {
  name: string;
  subTitle: string;
}

export interface IQuestion {
  tags: CourseTag[];
  question: string;
  answer: string | React.ReactNode;
  answerRaw: string;
}

export const courseDetails: Map<CourseTag, ICourseTagDetails> = new Map<
  CourseTag,
  ICourseTagDetails
>([
  [
    "BIO331",
    {
      name: "Animal Behavior",
      subTitle: "BIO331",
    },
  ],

  [
    "BIO360",
    {
      name: "Animal Physiology",
      subTitle: "BIO360",
    },
  ],

  [
    "BIO345",
    {
      name: "Evolution",
      subTitle: "BIO345",
    },
  ],

  [
    "BIO320",
    {
      name: "Fundamentals of Ecology",
      subTitle: "BIO320",
    },
  ],

  [
    "BIO181",
    {
      name: "General Biology 1",
      subTitle: "BIO181",
    },
  ],

  [
    "BIO340",
    {
      name: "General Genetics",
      subTitle: "BIO340",
    },
  ],

  [
    "BIO420|MIC420|MIC598",
    {
      name: "Immunology",
      subTitle: "BIO420 | MIC420 | MIC598",
    },
  ],

  [
    "BIO100",
    {
      name: "The Living World",
      subTitle: "BIO100",
    },
  ],

  [
    "BIO370",
    {
      name: "Vertebrate Zoology",
      subTitle: "BIO370",
    },
  ],
  [
    "CHM231",
    {
      name: "Elementary Organic Chemistry",
      subTitle: "CHM231",
    },
  ],
  [
    "CHM233",
    {
      name: "General Organic Chemistry I",
      subTitle: "CHM233",
    },
  ],
  [
    "CHM234",
    {
      name: "General Organic Chemistry II",
      subTitle: "CHM234",
    },
  ],
  [
    "AWS",
    {
      name: "AWS Cloud Foundations",
      subTitle: "CIS194",
    },
  ],
]);

export const allCoursesTags: CourseTag[] = [
  "BIO181",
  "BIO100",
  "AWS",
  "BIO345",
  "BIO370",
  "BIO420|MIC420|MIC598",
  "BIO320",
  "BIO340",
  "BIO360",
  "BIO331",
  "CHM231",
  "CHM233",
  "CHM234",
];

export const allCoursesTagsGraded: CourseTag[] = [
  "AWS",
  "BIO100",
  "BIO181",
  "BIO320",
  "BIO331",
  "BIO340",
  "BIO345",
  "BIO360",
  "BIO370",
  "BIO420|MIC420|MIC598",
];

export const eightCourseGrouping: CourseTag[] = [
  "BIO181",
  "BIO100",
  "AWS",
  "BIO345",
  "BIO370",
  "BIO340",
  "BIO320",
  "BIO331",
];

const faqCourseCategoryFilters = [
  { id: "all", label: "All", icon: "all" },
  { id: "bio-mic", label: "BIO/MIC", icon: "bio" },
  { id: "chm", label: "CHM", icon: "chm" },
  { id: "cis-aws", label: "CIS/AWS", icon: "cs" },
] as const;

type FaqCourseCategoryId = (typeof faqCourseCategoryFilters)[number]["id"];

const faqCourseTableRows = Array.from(courseDetails.entries());

const getCourseRowAriaLabel = (tag: CourseTag, details: ICourseTagDetails) =>
  getCourseAccessibleLabel(tag, details.name, details.subTitle);

const faqCourseMatchesCategory = (
  tag: CourseTag,
  categoryId: FaqCourseCategoryId
): boolean => {
  switch (categoryId) {
    case "all":
      return true;
    case "bio-mic":
      return tag.startsWith("BIO") || tag.includes("MIC");
    case "chm":
      return tag.startsWith("CHM");
    case "cis-aws":
      return tag === "AWS";
    default:
      return true;
  }
};

const faqCourseCategoryCounts = Object.fromEntries(
  faqCourseCategoryFilters.map(({ id }) => [
    id,
    faqCourseTableRows.filter(([tag]) => faqCourseMatchesCategory(tag, id)).length,
  ])
) as Record<FaqCourseCategoryId, number>;

const faqCourseTableDotSx = {
  width: 6,
  height: 6,
  borderRadius: "33554400px",
  backgroundColor: "#0092E0",
  flexShrink: 0,
  marginRight: "12px",
} as const;

const faqCourseCodeTypography = {
  color: "#484848",
  fontFamily: "Roboto",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "18px",
  transition: "color 300ms ease-out",
} as const;

const faqCourseTableRowChevronSx = {
  position: "absolute",
  right: 20,
  top: "50%",
  transform: "translateY(-50%)",
  color: "#0092E0",
  fontFamily: '"Font Awesome 6 Pro"',
  fontSize: "13px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "19.5px",
  opacity: 0,
  transition: "opacity 300ms ease-out",
  pointerEvents: "none",
} as const;

const faqCourseTableRowButtonSx = (backgroundColor: string) => ({
  display: "block",
  width: "100%",
  padding: 0,
  margin: 0,
  border: "none",
  backgroundColor,
  cursor: "pointer",
  textAlign: "left",
  font: "inherit",
  color: "inherit",
  appearance: "none",
  WebkitAppearance: "none",
  "&:focus-visible": {
    outline: "2px solid #007CBF",
    outlineOffset: "-2px",
    zIndex: 1,
  },
  "&:hover .faq-course-row-code": {
    color: "#191919",
  },
  "&:hover .faq-course-row-chevron": {
    opacity: 1,
  },
});

const faqCourseTableRowContentSx = (isMultiCode = false) => ({
  display: "flex",
  alignItems: isMultiCode ? "flex-start" : "center",
  width: "100%",
  pl: "12px",
  pr: 2,
  height: isMultiCode ? "auto" : "33.5px",
  py: isMultiCode ? "8px" : 0,
  boxSizing: "border-box",
  position: "relative",
});

const faqCourseTableDividerSx = {
  width: "1px",
  height: "12px",
  flexShrink: 0,
  backgroundColor: "#D0D0D0",
} as const;

const faqCourseNameTypography = {
  color: "#191919",
  fontFamily: "Roboto",
  fontSize: "13px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "19.5px",
} as const;

const faqCourseTableSx = {
  display: "flex",
  width: 682,
  maxWidth: "100%",
  flexDirection: "column",
  alignItems: "stretch",
  alignSelf: "stretch",
  border: "2px solid #D0D0D0",
  borderRadius: "16px",
  overflow: "hidden",
  backgroundColor: "#FFFFFF",
  boxSizing: "border-box",
} as const;

/** Same scroll pattern as `FAQCourseDropdown` list — global scrollbar from App.css. */
const faqCourseTableScrollSx = {
  maxHeight: 260,
  overflowY: "auto",
  listStyle: "none",
  margin: 0,
  padding: 0,
} as const;

const faqCourseTableColumnHeaderTypography = {
  color: "#484848",
  fontFamily: "Roboto",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "15px",
  letterSpacing: "1px",
  textTransform: "uppercase",
} as const;

const faqCourseTableHeaderEntrySx = {
  display: "flex",
  alignItems: "center",
  alignSelf: "stretch",
  width: "100%",
  height: "33.5px",
  pl: "12px",
  pr: 2,
  boxSizing: "border-box",
  flexShrink: 0,
  backgroundColor: "#FFFFFF",
  borderBottom: "2px solid #D0D0D0",
} as const;

const faqCourseTableFooterEntrySx = {
  display: "flex",
  alignItems: "center",
  alignSelf: "stretch",
  width: "100%",
  height: "33.5px",
  boxSizing: "border-box",
  flexShrink: 0,
  backgroundColor: "#FFFFFF",
  borderTop: "2px solid #D0D0D0",
} as const;

const faqCourseTableBodySx = {
  position: "relative",
  width: "100%",
  alignSelf: "stretch",
  flexShrink: 0,
} as const;

const faqCourseTableFadeOverlaySx = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: 40,
  pointerEvents: "none",
  background:
    "linear-gradient(0deg, rgba(255, 255, 255, 0.50) 0%, rgba(255, 255, 255, 0.00) 100%)",
  backdropFilter: "blur(1px)",
  WebkitBackdropFilter: "blur(1px)",
  maskImage: "linear-gradient(to top, #000 0%, transparent 100%)",
  WebkitMaskImage: "linear-gradient(to top, #000 0%, transparent 100%)",
} as const;

const faqCourseCategoryButtonSx = {
  display: "flex",
  height: "36px",
  minHeight: "36px",
  padding: "8px 16px",
  boxSizing: "border-box",
  alignItems: "center",
  gap: "4px",
  borderRadius: "999px",
  border: "2px solid #0092E0",
  background: "#FFF",
  textTransform: "none",
  color: "#0092E0",
  fontSize: "14px",
  lineHeight: "16px",
  minWidth: 0,
  boxShadow: "none",
  "&:hover": {
    borderRadius: "999px",
    border: "2px solid #0092E0",
    background: "#E2F7FE",
    color: "#0092E0",
    boxShadow: "none",
  },
  "&:active": {
    borderRadius: "999px",
    border: "2px solid #0092E0",
    background: "#C7EBFF",
    color: "#0092E0",
    boxShadow: "none",
  },
};

const faqCourseCategoryButtonSelectedSx = {
  borderRadius: "999px",
  border: "2px solid #0092E0",
  background: "#0092E0",
  color: "#FFFFFF",
  "&:hover": {
    borderRadius: "999px",
    border: "2px solid #0092E0",
    background: "#0092E0",
    color: "#FFFFFF",
    boxShadow: "none",
  },
  "&:active": {
    borderRadius: "999px",
    border: "2px solid #0092E0",
    background: "#0092E0",
    color: "#FFFFFF",
    boxShadow: "none",
  },
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    flexGrow: 1,
    minHeight: 0,
    backgroundColor: "#303030",
    position: "relative",
    overflow: "auto",
    backgroundImage: `url(${BGLeft}), url(${BGRight})`,
    backgroundPosition: "bottom left, top right",
    backgroundRepeat: "no-repeat, no-repeat",
    backgroundSize: "auto, auto",
    display: "flex",
  },
  contentContainer: {
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    minHeight: "100%",
    overflow: "auto",
  },
  heading: {
    textAlign: "center",
    lineHeight: "52px",
    [theme.breakpoints.down("md")]: {
      fontSize: "28px",
      lineHeight: "36px",
    },
  },
  landingHeading: {
    textAlign: "left",
    lineHeight: "36px",
    color: "#FFFFFF",
    [theme.breakpoints.down("md")]: {
      fontSize: "28px",
      lineHeight: "36px",
    },
  },
  contactSupport: {
    color: "#474747",
    marginTop: "32px",
    padding: "0px 112px",
    [theme.breakpoints.down("md")]: {
      padding: "0px",
    },
    lineHeight: "28px",
    fontWeight: 500,
    fontSize: "16px",
  },
  imageContainer: {
    padding: "24px 0px 0px 0px",
    [theme.breakpoints.down("md")]: {
      padding: "0px",
      paddingTop: "16px",
    },
  },
  imageNoteContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "4px",
    marginBottom: "24px",
  },
  imageNote: {
    flexGrow: 1,
  },
  paragraph: {
    marginTop: "0px",
    marginBottom: "16px",
  },
}));

export const FAQPage = ({
  singleCourseTag,
  search,
  currentCourse,
}: {
  singleCourseTag?: boolean;
  search?: boolean;
  currentCourse?: Course;
}) => {
  const params = useParams<{ courseTag?: CourseTag }>();
  const history = useHistory();
  const location = useLocation<{ focusFaqContent?: boolean }>();
  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [inputText, setInputText] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<CourseTag[]>(
    singleCourseTag && params.courseTag ? [params.courseTag] : []
  );
  const [selectedCourse, setSelectedCourse] = useStorageState({
    storageKey: "faq-lastSceneCourse",
    initialValue:
      params.courseTag ??
      (localStorage.getItem("lxp-lastCourseFaqTag") as CourseTag | null) ??
      currentCourse?.metadata.faq_course_tag,
  });

  const onSearch = (e: any) => {
    e.preventDefault();
    setSearchText(inputText);
    if (!search && !singleCourseTag) {
      history.push("/faq/search");
    }
  };
  // FAQs are currently only for higher-ed courses
  const supportLink = getSupportLink(true);

  const fullContent: ISection[] = [
    {
      id: "topic-technical-issues",
      headingText: "Technical issues",
      headingIcon: <i className="fa-solid fa-wrench"></i>,
      questions: [
        {
          tags: allCoursesTags,
          question: "My assignment won't load when I try to access it.",
          answerRaw: `Try these common troubleshooting steps to resolve most technical issues:
            View the content on a laptop or desktop.
            Use Google Chrome as your browser.
            Make sure you are not using the Canvas application when trying to access Digit. Digit must be accessed directly through a web browser such as Chrome.
            Disable any ad blockers.
            Clear your browser cache and cookies.
            Restart your device and try again.
            If you're still having trouble, get in touch with us here, or call ASU’s Experience Center at 1-480-965-6500 or 1-855-278-5080 for urgent support.`,
          answer: (
            <>
              Try these common troubleshooting steps to resolve most technical issues:
              <ul style={{ marginTop: "0px", marginBottom: "0px" }}>
                <li>View the content on a laptop or desktop.</li>
                <li>Use Google Chrome as your browser.</li>
                <li>
                  Make sure you are not using the Canvas application when trying to access
                  Digit. Digit must be accessed directly through a web browser such as
                  Chrome.
                </li>
                <li>
                  <Link
                    sx={{ color: "#474747" }}
                    target="_blank"
                    href="https://support.google.com/chrome/answer/7632919?hl=en&co=GENIE.Platform%3DDesktop">
                    Disable any ad blockers
                  </Link>
                  .
                </li>
                <li>
                  <Link
                    sx={{ color: "#474747" }}
                    target="_blank"
                    href="https://support.google.com/accounts/answer/32050?hl=en&co=GENIE.Platform%3DDesktop">
                    Clear your browser cache and cookies
                  </Link>
                  .
                </li>
                <li>
                  Restart your device and try again.
                  <ul style={{ marginTop: "0px", marginBottom: "0px" }}>
                    <li style={{ listStyle: "disc" }}>
                      If you're still having trouble,{" "}
                      <Link sx={{ color: "#474747" }} target="_blank" href={supportLink}>
                        get in touch with us here
                      </Link>
                      , or call ASU’s Experience Center at <b>1-480-965-6500</b> or{" "}
                      <b>1-855-278-5080</b> for urgent support.
                    </li>
                  </ul>
                </li>
              </ul>
            </>
          ),
        },
        {
          tags: allCoursesTags,
          question: "I am seeing a Session Expiration page.",
          answerRaw: `Digit sessions automatically expire three hours after you launch an assignment, and you can extend your session for an additional three hours if needed. This helps keep your data secure. If you’re seeing the Session Expiration page right after launching Digit, try the following steps:
          Check that your computer’s date and time are set automatically and use a standard time zone (not a manually set time).
          Try launching Digit in Chrome Incognito mode or a similar private browsing mode.
          Make sure cookies are enabled in your browser settings.
          `,
          answer: (
            <>
              Digit sessions automatically expire three hours after you launch an
              assignment, and you can extend your session for an additional three hours if
              needed. This helps keep your data secure. If you’re seeing the Session
              Expiration page right after launching Digit, try the following steps:
              <ul style={{ marginTop: "0px", marginBottom: "0px" }}>
                <li>
                  Check that your computer’s date and time are set automatically and use a
                  standard time zone (not a manually set time).
                </li>
                <li>
                  Try launching Digit in Chrome Incognito mode or a similar private
                  browsing mode.
                </li>
                <li>Make sure cookies are enabled in your browser settings.</li>
              </ul>
            </>
          ),
        },
        {
          tags: allCoursesTags,
          question:
            "Who do I contact for reporting technical issues or general feedback?",
          answerRaw: `To get the quickest response, you can use the Feedback & Help option, which is available on the Digit home page and module pages:
          You can also use the Help button directly within the lesson activities:          
          Shown above: Digit Help Button          
          The support links will take you to either the Digit support request form to submit any feedback/technical issues directly to our team, or to the ASU Experience Center, depending on the time of day that you access the link. You can always call the Experience Center at 1-480-965-6500 or 1-855-278-5080, especially for urgent support.
          `,
          answer: (
            <>
              <div className={classes.paragraph}>
                To get the quickest response, you can use the Feedback & Help option,
                which is available on the Digit home page and module pages:
                <div className={classes.imageContainer}>
                  <img
                    style={{ width: "100%", height: "auto" }}
                    src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/faq-screen-feedbackhelp.png"
                    alt="feedback & help screenshot"
                  />
                </div>
              </div>
              <div className={classes.paragraph}>
                You can also use the Help button directly within the lesson activities:
                <div className={classes.imageContainer}>
                  <img
                    style={{ width: "100%", height: "auto" }}
                    src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/faq-screen-orchard-help.png"
                    alt="Orchard feedback & help screenshot"
                  />
                  <div className={classes.imageNoteContainer}>
                    <div className={classes.imageNote}>
                      <span style={{ fontWeight: "bold" }}>
                        Shown above: Digit Help Button
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.paragraph}>
                The support links will take you to either the Digit support request form
                to submit any feedback/technical issues directly to our team, or to the
                ASU Experience Center, depending on the time of day that you access the
                link. You can always call the Experience Center at{" "}
                <span style={{ fontWeight: "bold" }}>1-480-965-6500</span> or{" "}
                <span style={{ fontWeight: "bold" }}>1-855-278-5080</span>, especially for
                urgent support.
              </div>
            </>
          ),
        },
        {
          tags: allCoursesTags,
          question: "I am stuck on the Terms of Use page.",
          answerRaw: `Make sure you scroll all the way to the bottom of the text, and check “I Consent”. You must accept Digit’s Terms of Use in order to use Digit.`,
          answer: (
            <>
              <div className={classes.paragraph}>
                Make sure you scroll all the way to the bottom of the text, and check “I
                Consent”. You must accept Digit’s Terms of Use in order to use Digit.
              </div>
            </>
          ),
        },
      ],
    },
    {
      id: "topic-learning-activity-concerns",
      headingText: "Learning activity concerns",
      headingIcon: <i className="fa-solid fa-question-circle"></i>,
      questions: [
        {
          tags: allCoursesTagsGraded,
          question: "I can't submit my activity answers.",
          answerRaw: `Check if you've answered all questions. Use the “Back” and “Forward” navigation buttons/arrows to review. Once every question is answered, the Submit/“Finish” button will appear.`,
          answer: (
            <>
              Check if you've answered all questions. Use the “Back” and “Forward”
              navigation buttons/arrows to review. Once every question is answered, the
              Submit/“Finish” button will appear.
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/cant-submit-activity.gif"
                  alt="submitting activity recording"
                />
              </div>
            </>
          ),
        },
        {
          tags: eightCourseGrouping,
          question: "I want to view correct answers after completing an activity.",
          answerRaw:
            "Click the ”Show Solution” button in the pop-up box that appears after you submit your answer choices to see correct answers and your responses. This option is only available immediately after completing the activity; if you navigate away you will have to complete the activity again for the “Show Solution” button to display again.",
          answer: (
            <>
              Click the ”Show Solution” button in the pop-up box that appears after you
              submit your answer choices to see correct answers and your responses. This
              option is only available immediately after completing the activity; if you
              navigate away you will have to complete the activity again for the “Show
              Solution” button to display again.
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/review-more-and-explore.png"
                  alt="more review modal screenshot"
                />
              </div>
            </>
          ),
        },
        {
          tags: ["BIO420|MIC420|MIC598", "BIO360"],
          question: "I want to view correct answers after completing an activity.",
          answerRaw: `You can see immediate feedback about your answer by clicking the “Check” button for each question. Only your max score of the graded attempts will count toward your course grade, but you have unlimited opportunities to complete a quiz and receive correctness feedback.
            After you meet the limited graded attempts, or after the assignment due date, you will be able to see all answers by using the “Show Solution” button.
            Shown above: Check button
            Shown above: Show Solution (only available after graded attempt limit is reached or assignment due date has passed)
            `,
          answer: (
            <>
              <div className={classes.paragraph}>
                You can see immediate feedback about your answer by clicking the “Check”
                button for each question. Only your max score of the graded attempts will
                count toward your course grade, but you have unlimited opportunities to
                complete a quiz and receive correctness feedback.
              </div>
              <div className={classes.paragraph}>
                After you meet the limited graded attempts, or after the assignment due
                date, you will be able to see all answers by using the “Show Solution”
                button.
              </div>
              <div className={classes.paragraph}>
                <div className={classes.imageContainer}>
                  <img
                    style={{ width: "100%", height: "auto" }}
                    src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/faq-screen-orchard-checkwork.png"
                    alt="Orchard check button"
                  />
                  <div className={classes.imageNoteContainer}>
                    <div className={classes.imageNote}>
                      <span style={{ fontWeight: "bold" }}>
                        Shown above: Check button
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.paragraph}>
                <div className={classes.imageContainer}>
                  <img
                    style={{ width: "100%", height: "auto" }}
                    src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/faq-screen-orchard-show-solution.png"
                    alt="Orchard show solution button"
                  />
                  <div className={classes.imageNoteContainer}>
                    <div className={classes.imageNote}>
                      <span style={{ fontWeight: "bold" }}>
                        Shown above: Show Solution (only available after graded attempt
                        limit is reached or assignment due date has passed)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ),
        },
        {
          tags: allCoursesTagsGraded,
          question: "My activity results show 15/15 correct for only 7 questions.",
          answerRaw:
            "Some questions require multiple answers, each counting towards your total score.",
          answer: (
            <>
              Some questions require multiple answers, each counting towards your total
              score.
            </>
          ),
        },
        // {
        //   tags: ["BIO370"],
        //   question:
        //     "I completed everything in the lesson module, but it still isn’t marked as complete. What should I do?",
        //   answerRaw: `If you selected “Yes” when asked whether you wanted to unlock additional learning for any activity in the lesson, you opened part of the lesson's secondary learning path. By default, this content is optional and appears in the Learning Activities panel with a plus (+) symbol.
        //     To fully complete the module, you’ll need to follow these steps:
        //     Click the plus (+) next to the learning activity whose additional learning you unlocked additional learning to again open the secondary path.
        //     Complete the additional learning activities in that support path. In most cases, completing one activity is enough.
        //     Once finished, click “Next Activity” to continue and trigger lesson completion.
        //     Once a secondary path is unlocked, Digit requires you to complete one of its items before the lesson will be marked "Complete."
        //     `,
        //   answer: (
        //     <>
        //       If you selected <span style={{ fontWeight: "bold" }}>“Yes”</span> when asked
        //       whether you wanted to unlock additional learning for any activity in the
        //       lesson, you opened part of the lesson's secondary learning path. By default,
        //       this content is optional and appears in the Learning Activities panel with a{" "}
        //       <span style={{ fontWeight: "bold" }}>plus (+) symbol</span>. To fully
        //       complete the module, you’ll need to follow these steps:
        //       <ul style={{ marginTop: "0px", marginBottom: "0px" }}>
        //         <li>
        //           Click the <span style={{ fontWeight: "bold" }}>plus (+)</span> next to
        //           the learning activity whose additional learning you unlocked additional
        //           learning to again open the secondary path.
        //         </li>
        //         <li>
        //           Complete the additional learning activities in that support path. In
        //           most cases, completing one activity is enough.
        //         </li>
        //         <li>
        //           Once finished, click{" "}
        //           <span style={{ fontWeight: "bold" }}>“Next Activity”</span> to continue
        //           and trigger lesson completion.
        //         </li>
        //       </ul>
        //       Once a secondary path is unlocked, Digit requires you to complete one of its
        //       items before the lesson will be marked "Complete."
        //     </>
        //   ),
        // },
        {
          tags: allCoursesTagsGraded,
          question: "I found a content error or discrepancy.",
          answerRaw:
            "If you think you found an error or discrepancy in a learning activity (e.g., a correct answer is marked as incorrect or an incorrect answer is marked as correct in the Solutions, the video explaining an activity presents a different solution than what was in the activity itself, or a presenter in a video says something different than what is on the slide being displayed), please report it to us here so that the issue can be investigated. The team will consult with the course developers to determine whether or not there is an error and will take all necessary follow-up steps. Regardless of how the issue is resolved, we truly appreciate your helping us maintain the accuracy and overall quality of content in Digit",
          answer: (
            <>
              <>
                If you think you found an error or discrepancy in a learning activity
                (e.g., a correct answer is marked as incorrect or an incorrect answer is
                marked as correct in the Solutions, the video explaining an activity
                presents a different solution than what was in the activity itself, or a
                presenter in a video says something different than what is on the slide
                being displayed),{" "}
                <Link sx={{ color: "#474747" }} target="_blank" href={supportLink}>
                  please report it to us here
                </Link>{" "}
                so that the issue can be investigated. The team will consult with the
                course developers to determine whether or not there is an error and will
                take all necessary follow-up steps. Regardless of how the issue is
                resolved, we truly appreciate your helping us maintain the accuracy and
                overall quality of content in Digit.
              </>
              {/* <>
                If you think you found an error or discrepancy in a learning activity
                (e.g., a correct answer is marked as incorrect or an incorrect answer is
                marked as correct in the Solutions OR the video explaining an activity
                presents a different solution than what was in the activity itself, or a
                presenter says something different than what is on the slide being
                displayed),{" "}
                <Link
                  sx={{ color: "#474747" }}
                  target="_blank"
                  href={supportLink}>
                  please report it to us here
                </Link>{" "}
                so that the issue can be investigated. The team will consult with the
                course developers to determine whether or not there is an error.
                <ul style={{ marginTop: "0px", marginBottom: "0px" }}>
                  <li>
                    If there is an error and it can be addressed quickly, it will be
                    corrected and you will be notified when the correction is complete.
                  </li>
                  <li>
                    If there is an error but it cannot be addressed until the course as a
                    whole is revised, we will document the issue and you will be notified
                    that the issue is logged for future action.
                  </li>
                  <li>
                    If we are able to confirm that the content is correct, we will refer
                    you to your course team to ensure that any misunderstandings are
                    addressed.
                  </li>
                </ul>
                Regardless of how the issue is resolved, we truly appreciate your
                helping us maintain the accuracy and overall quality of content in Digit.
              </> */}
            </>
          ),
        },
      ],
    },
    {
      id: "topic-assignments-and-grading",
      headingText: "Assignments and grading",
      headingIcon: <i className="fa-solid fa-file-pen"></i>,
      questions: [
        {
          tags: allCoursesTagsGraded,
          question: "I am unable to move onto the next activity within the lesson.",
          answerRaw:
            "Make sure that you have completed your current activity before trying to move onto the next one. This can mean watching a video to the end, answering every question in an interactive activity, or scrolling to the bottom of the page (i.e., for the cursor to  actually touch the bottom of the page). You will know the activity is complete when a check mark replaces the activity number, as shown below:",
          answer: (
            <>
              Make sure that you have completed your current activity before trying to
              move onto the next one. This can mean watching a video to the end, answering
              every question in an interactive activity, or scrolling to the bottom of the
              page (i.e., for the cursor to actually touch the bottom of the page). You
              will know the activity is complete when a check mark replaces the activity
              number, as shown below:
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/learning-map-screenshot.png"
                  alt="learning map screenshot"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Left: </span> Learning activities
                    not complete.
                  </div>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Right: </span> Learning
                    activities complete.
                  </div>
                </div>
              </div>
            </>
          ),
        },
        {
          tags: eightCourseGrouping,
          question:
            "My grade isn't showing in Canvas after completing a Digit assignment.",
          answerRaw: `To receive credit for a lesson in Digit, and for your grade to appear in Canvas, you must complete all required activities on the primary learning path. These are the items not indented in the lesson’s navigation pane.
          Make sure you do the following depending on the activity:
          Watch all lesson videos to the end
          Scroll to the bottom of every lesson page
          Attempt all Practice or similar interactive activities
          Reminder: Activities in the secondary learning path (indented items), such as Additional Practice, Worked Example videos (if available), and Additional Learning readings or videos, are optional and do not count toward lesson completion.
          A lesson will only be marked as complete when the word "Completed" and the bullseye icon appear at the bottom of the learning activities list (see images below for reference). Completed activities have a check mark instead of a number next to the name of the activity.
          If everything is completed and your grades are still not showing up in Canvas, please try re-launching the same assignment via Canvas and waiting a few minutes. If your grade is still not updated, please get in touch with us here or call the ASU Experience center at 1-480-965-6500 or 1-855-278-5080 for evening and weekend support.`,
          answer: (
            <>
              <div className={classes.paragraph}>
                To receive credit for a lesson in Digit, and for your grade to appear in
                Canvas, you must complete{" "}
                <b>all required activities on the primary learning path</b>. These are the
                items <b>not indented</b> in the lesson’s navigation pane.
              </div>
              <div className={classes.paragraph}>
                Make sure you do the following depending on the activity:
              </div>
              <ul className={classes.paragraph}>
                <li>Watch all lesson videos to the end</li>
                <li>Scroll to the bottom of every lesson page</li>
                <li>Attempt all Practice or similar interactive activities</li>
              </ul>
              <div className={classes.paragraph}>
                <b>Reminder:</b> Activities in the secondary learning path (indented
                items), such as Additional Practice, Worked Example videos (if available),
                and Additional Learning readings or videos, are optional and do not count
                toward lesson completion.
              </div>
              <div>
                A lesson will only be marked as complete when the word <b>"Completed"</b>{" "}
                and the <b>bullseye icon</b> appear at the bottom of the learning
                activities list (see images below for reference). Completed activities
                have a check mark instead of a number next to the name of the activity.
              </div>
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/learning-map-screenshot.png"
                  alt="learning map screenshot"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Left: </span> Learning activities
                    not complete.
                  </div>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Right: </span> Learning
                    activities complete.
                  </div>
                </div>
              </div>
              <div className={classes.paragraph}>
                If everything is completed and your grades are still not showing up in
                Canvas, please try re-launching the same assignment via Canvas and waiting
                a few minutes. If your grade is still not updated, please{" "}
                <Link sx={{ color: "#474747" }} target="_blank" href={supportLink}>
                  get in touch with us here
                </Link>{" "}
                or call the ASU Experience center at{" "}
                <span style={{ fontWeight: "bold" }}>1-480-965-6500</span> or{" "}
                <span style={{ fontWeight: "bold" }}>1-855-278-5080</span> for urgent
                support.
              </div>
            </>
          ),
        },
        {
          tags: ["BIO420|MIC420|MIC598", "BIO360"],
          question:
            "My grade isn't showing in Canvas after completing a Digit assignment.",
          answerRaw: `Your Canvas grade will not update until after you complete the entire Digit module, which includes completing all graded quiz activities.
          To receive credit for a lesson in Digit, and for your grade to appear in Canvas, you must complete all required activities on the primary learning path. These are the items not indented in the lesson’s navigation pane.
          Make sure you do the following depending on the activity:
          Watch all lesson videos to the end
          Scroll to the bottom of every lesson page
          Attempt all Practice or similar interactive activities
          Attempt the graded quiz
          Reminder: Activities in the secondary learning path (indented items), such as Additional Practice, Worked Example videos (if available), and Additional Learning readings or videos, are optional and do not count toward lesson completion.
          A lesson will only be marked as complete when the word "Completed" and the bullseye icon appear at the bottom of the learning activities list (see images below for reference). Completed activities have a check mark instead of a number next to the name of the activity.
          Left: Learning activities not complete. Right: Learning activities complete.
          If everything is completed and your grades are still not showing up in Canvas, please try re-launching the same assignment via Canvas and waiting a few minutes. If your grade is still not updated, please get in touch with us here or call the ASU Experience center at 1-480-965-6500 or 1-855-278-5080 for evening and weekend support.`,
          answer: (
            <>
              <div className={classes.paragraph}>
                Your Canvas grade will not update until after you complete the entire
                Digit module, which includes completing all graded quiz activities.
              </div>
              <div className={classes.paragraph}>
                To receive credit for a lesson in Digit, and for your grade to appear in
                Canvas, you must complete all required activities on the primary learning
                path. These are the items not indented in the lesson’s navigation pane.
              </div>
              <div className={classes.paragraph}>
                Make sure you do the following depending on the activity:
              </div>
              <ul className={classes.paragraph}>
                <li>Watch all lesson videos to the end</li>
                <li>Scroll to the bottom of every lesson page</li>
                <li>Attempt all Practice or similar interactive activities</li>
                <li>Attempt the graded quiz</li>
              </ul>
              <div className={classes.paragraph}>
                <b>Reminder:</b> Activities in the secondary learning path (indented
                items), such as Additional Practice, Worked Example videos (if available),
                and Additional Learning readings or videos, are optional and do not count
                toward lesson completion.
              </div>
              <div>
                A lesson will only be marked as complete when the word <b>"Completed"</b>{" "}
                and the <b>bullseye icon</b> appear at the bottom of the learning
                activities list (see images below for reference). Completed activities
                have a check mark instead of a number next to the name of the activity.
              </div>
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/learning-map-screenshot.png"
                  alt="learning map screenshot"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Left: </span> Learning activities
                    not complete.
                  </div>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Right: </span> Learning
                    activities complete.
                  </div>
                </div>
              </div>
              If everything is completed and your grades are still not showing up in
              Canvas, please try re-launching the same assignment via Canvas and waiting a
              few minutes. If your grade is still not updated, please{" "}
              <Link sx={{ color: "#474747" }} target="_blank" href={supportLink}>
                get in touch with us here
              </Link>{" "}
              or call the ASU Experience center at{" "}
              <span style={{ fontWeight: "bold" }}>1-480-965-6500</span> or{" "}
              <span style={{ fontWeight: "bold" }}>1-855-278-5080</span> for evening and
              weekend support.
            </>
          ),
        },
        {
          tags: ["BIO181", "BIO345", "AWS"],
          question: "I don’t understand how my grade is calculated in Digit.",
          answerRaw: `Your grade is calculated based on working through and completing an entire Digit module. In order to receive 100%, all lessons must be completed before the Canvas assignment due date (if there is one). Your grade will be updated as you progress through the module (for example, if you completed half of the lessons in a module, your grade should be 50% for that module, and so on).
          Shown above: Digit module assignment.
          To receive credit for a lesson in Digit, and for your grade to appear in Canvas, you must complete all required activities on the primary learning path. These are the items not indented in the lesson’s navigation pane.
          Make sure you do the following depending on the activity:
          Watch all lesson videos to the end
          Scroll to the bottom of every lesson page
          Attempt all Practice or similar interactive activities
          Attempt the graded quiz
          Reminder: Activities in the secondary learning path (indented items), such as Additional Practice, Worked Example videos (if available), and Additional Learning readings or videos, are optional and do not count toward lesson completion.
          A lesson will only be marked as complete when the word "Completed" and the bullseye icon appear at the bottom of the learning activities list (see images below for reference). Completed activities have a check mark instead of a number next to the name of the activity.
          Left: Learning activities not complete. Right: Learning activities complete.
          `,
          answer: (
            <>
              Your grade is calculated based on working through and completing an entire
              Digit module. In order to receive 100%, all lessons must be completed before
              the Canvas assignment due date (if there is one). Your grade will be updated
              as you progress through the module (for example, if you completed half of
              the lessons in a module, your grade should be 50% for that module, and so
              on).
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/faq-screen-module-example.png"
                  alt="Digit module assignment"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>
                      Shown above: Digit module assignment.
                    </span>
                  </div>
                </div>
              </div>
              <div className={classes.paragraph}>
                To receive credit for a lesson in Digit, and for your grade to appear in
                Canvas, you must complete{" "}
                <b>all required activities on the primary learning path</b>. These are the
                items <b>not indented</b> in the lesson’s navigation pane.
              </div>
              <div className={classes.paragraph}>
                Make sure you do the following depending on the activity:
              </div>
              <ul className={classes.paragraph}>
                <li>Watch all lesson videos to the end</li>
                <li>Scroll to the bottom of every lesson page</li>
                <li>Attempt all Practice or similar interactive activities</li>
                <li>Attempt the graded quiz</li>
              </ul>
              <div className={classes.paragraph}>
                <b>Reminder:</b> Activities in the secondary learning path (indented
                items), such as Additional Practice, Worked Example videos (if available),
                and Additional Learning readings or videos, are optional and do not count
                toward lesson completion.
              </div>
              <div>
                A lesson will only be marked as complete when the word <b>"Completed"</b>{" "}
                and the <b>bullseye icon</b> appear at the bottom of the learning
                activities list (see images below for reference). Completed activities
                have a check mark instead of a number next to the name of the activity.
              </div>
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/learning-map-screenshot.png"
                  alt="learning map screenshot"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Left: </span> Learning activities
                    not complete.
                  </div>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Right: </span> Learning
                    activities complete.
                  </div>
                </div>
              </div>
            </>
          ),
        },
        {
          tags: ["BIO100", "BIO370", "BIO320", "BIO340", "BIO331"],
          question: "I don’t understand how my grade is calculated in Digit.",
          answerRaw: `Your grade is calculated based on working through and completing individual Digit lessons. In order to receive 100%, your lesson must be completed before the assignment due date (if there is one). Your grade should be reflected in Canvas shortly after completing the Digit lesson.
          Shown above: Digit lesson assignment.
          To receive credit for a lesson in Digit, and for your grade to appear in Canvas, you must complete all required activities on the primary learning path. These are the items not indented in the lesson’s navigation pane.
          Make sure you do the following depending on the activity:
          Watch all lesson videos to the end
          Scroll to the bottom of every lesson page
          Attempt all Practice or similar interactive activities
          Attempt the graded quiz
          Reminder: Activities in the secondary learning path (indented items), such as Additional Practice, Worked Example videos (if available), and Additional Learning readings or videos, are optional and do not count toward lesson completion.
          A lesson will only be marked as complete when the word "Completed" and the bullseye icon appear at the bottom of the learning activities list (see images below for reference). Completed activities have a check mark instead of a number next to the name of the activity.
          Left: Learning activities not complete. Right: Learning activities complete.
          `,
          answer: (
            <>
              Your grade is calculated based on working through and completing individual
              Digit lessons. In order to receive 100%, your lesson must be completed
              before the assignment due date (if there is one). Your grade should be
              reflected in Canvas shortly after completing the Digit lesson.
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/bio-screen-target-example.png"
                  alt=""
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>
                      Shown above: Digit lesson assignment.
                    </span>
                  </div>
                </div>
              </div>
              <div className={classes.paragraph}>
                To receive credit for a lesson in Digit, and for your grade to appear in
                Canvas, you must complete{" "}
                <b>all required activities on the primary learning path</b>. These are the
                items <b>not indented</b> in the lesson’s navigation pane.
              </div>
              <div className={classes.paragraph}>
                Make sure you do the following depending on the activity:
              </div>
              <ul className={classes.paragraph}>
                <li>Watch all lesson videos to the end</li>
                <li>Scroll to the bottom of every lesson page</li>
                <li>Attempt all Practice or similar interactive activities</li>
                <li>Attempt the graded quiz</li>
              </ul>
              <div className={classes.paragraph}>
                <b>Reminder:</b> Activities in the secondary learning path (indented
                items), such as Additional Practice, Worked Example videos (if available),
                and Additional Learning readings or videos, are optional and do not count
                toward lesson completion.
              </div>
              <div>
                A lesson will only be marked as complete when the word <b>"Completed"</b>{" "}
                and the <b>bullseye icon</b> appear at the bottom of the learning
                activities list (see images below for reference). Completed activities
                have a check mark instead of a number next to the name of the activity.
              </div>
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/learning-map-screenshot.png"
                  alt="learning map screenshot"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Left: </span> Learning activities
                    not complete.
                  </div>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Right: </span> Learning
                    activities complete.
                  </div>
                </div>
              </div>
            </>
          ),
        },
        {
          tags: ["BIO420|MIC420|MIC598", "BIO360"],
          question: "I don’t understand how my grade is calculated in Digit.",
          answerRaw: `Your grade is calculated based on the graded quiz scores earned while working through and completing an entire Digit module. In order to receive 100%, all lessons must be completed before the assignment due date (if there is one). Your grade will be updated once you complete all lessons within the Digit module. Your score is calculated based on how many points total are available throughout all the lessons. For example, for a module with two lessons, where one lesson has a graded quiz worth 5 points and the other worth 3 points, your grade would be the total points earned divided by the total points possible (8 in this example). Some lessons may not contain a graded quiz activity–in these cases, the lessons will not impact your Canvas grade, but reviewing the material in these lessons is vital to understanding the concepts presented in the graded quizzes.
          Shown above: Digit module assignment with both graded and non-graded lessons.
          To receive credit for a lesson in Digit, and for your grade to appear in Canvas, you must complete all required activities on the primary learning path. These are the items not indented in the lesson’s navigation pane.
          Make sure you do the following depending on the activity:
          Watch all lesson videos to the end
          Scroll to the bottom of every lesson page
          Attempt all Practice or similar interactive activities
          Attempt the graded quiz
          Reminder: Activities in the secondary learning path (indented items), such as Additional Practice, Worked Example videos (if available), and Additional Learning readings or videos, are optional and do not count toward lesson completion.
          A lesson will only be marked as complete when the word "Completed" and the bullseye icon appear at the bottom of the learning activities list (see images below for reference). Completed activities have a check mark instead of a number next to the name of the activity.`,
          answer: (
            <>
              Your grade is calculated based on the graded quiz scores earned while
              working through and completing an entire Digit module. In order to receive
              100%, all lessons must be completed before the assignment due date (if there
              is one). Your grade will be updated once you complete all lessons within the
              Digit module. Your score is calculated based on how many points total are
              available throughout all the lessons. For example, for a module with two
              lessons, where one lesson has a graded quiz worth 5 points and the other
              worth 3 points, your grade would be the total points earned divided by the
              total points possible (8 in this example). Some lessons may not contain a
              graded quiz activity–in these cases, the lessons will not impact your Canvas
              grade, but reviewing the material in these lessons is vital to understanding
              the concepts presented in the graded quizzes.
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/faq-screen-module-quiz-example.png"
                  alt="Digit module assignment with both graded and non-graded lessons"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>
                      Shown above: Digit module assignment with both graded and non-graded
                      lessons.
                    </span>
                  </div>
                </div>
              </div>
              <div className={classes.paragraph}>
                To receive credit for a lesson in Digit, and for your grade to appear in
                Canvas, you must complete all required activities on the primary learning
                path. These are the items not indented in the lesson’s navigation pane.
              </div>
              <div className={classes.paragraph}>
                Make sure you do the following depending on the activity:
              </div>
              <ul className={classes.paragraph}>
                <li>Watch all lesson videos to the end</li>
                <li>Scroll to the bottom of every lesson page</li>
                <li>Attempt all Practice or similar interactive activities</li>
                <li>Attempt the graded quiz</li>
              </ul>
              <div className={classes.paragraph}>
                <b>Reminder:</b> Activities in the secondary learning path (indented
                items), such as Additional Practice, Worked Example videos (if available),
                and Additional Learning readings or videos, are optional and do not count
                toward lesson completion.
              </div>
              <div>
                A lesson will only be marked as complete when the word <b>"Completed"</b>{" "}
                and the <b>bullseye icon</b> appear at the bottom of the learning
                activities list (see images below for reference). Completed activities
                have a check mark instead of a number next to the name of the activity.
              </div>
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/learning-map-screenshot.png"
                  alt="learning map screenshot"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Left: </span> Learning activities
                    not complete.
                  </div>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>Right: </span> Learning
                    activities complete.
                  </div>
                </div>
              </div>
              You can tell which of the practice quizzes is being counted towards your
              grade by the “Graded” tag on the quiz, as shown below:
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/faq-screen-module-quiz-orchard-example.png"
                  alt="A quiz with the “Graded” tag"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>
                      Shown above: A quiz with the “Graded” tag.
                    </span>
                  </div>
                </div>
              </div>
            </>
          ),
        },
        {
          tags: allCoursesTagsGraded,
          question:
            "I was logged out of my Digit assignment, and I’m concerned that my progress was not saved.",
          answerRaw: `Your progress is saved even if you're logged out. Please relaunch the assignment from Canvas to continue.`,
          answer: (
            <>
              Your progress is saved even if you're logged out. Please relaunch the
              assignment from Canvas to continue.
            </>
          ),
        },
        {
          tags: eightCourseGrouping,
          question:
            "How do I know which scores are reported at the end of each lesson so that I can see how they affect my grade?",
          answerRaw: `The scores are generated after you complete each lesson. This score is the average of a lesson’s required learning activities (additional learning activities do not count towards it). Your “grade” for Digit assignments is based only on completion, so the scores themselves do not affect your overall grade in Canvas. Instead, these scores should be used to help you identify which lessons you may need to review to better understand the material and be better prepared for exams, graded quizzes, and other graded assignments.
          There may be some lessons that do not contain any practice activities, depending on your course. When there are no practice activities, you will receive full credit for reviewing all material in the lesson.`,
          answer: (
            <>
              <div className={classes.paragraph}>
                The scores are generated after you complete each lesson. This score is the
                average of a lesson’s required learning activities (additional learning
                activities do not count towards it). Your “grade” for Digit assignments is
                based only on <span style={{ fontWeight: "bold" }}>completion</span>, so
                the scores themselves do not affect your overall grade in Canvas. Instead,
                these scores should be used to help you identify which lessons you may
                need to review to better understand the material and be better prepared
                for exams, graded quizzes, and other graded assignments.
              </div>
              <div className={classes.paragraph}>
                There may be some lessons that do not contain any practice activities,
                depending on your course. When there are no practice activities, you will
                receive full credit for reviewing all material in the lesson.
              </div>
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", maxWidth: "400px", height: "auto" }}
                  src="https://macs-assets.s3.amazonaws.com/digit-biospine-completion-example.png"
                  alt="lesson completion example screenshot"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>
                      Shown above: The average scores earned for each lesson within a
                      Digit module.
                    </span>
                  </div>
                </div>
              </div>
            </>
          ),
        },
        {
          tags: ["BIO420|MIC420|MIC598", "BIO360"],
          question:
            "My score isn’t going up, even though I got a higher quiz score this time.",
          answerRaw: `Even though you can retry the lesson quizzes as many times as you like, your final graded score will not update after one of the following happens:
          You reached the maximum attempt limits for the quiz.
          The Canvas assignment is past due.          
          If your score isn’t changing, check the quiz’s due date in Canvas to see if it’s already past due, and also check if you’ve used up all your attempts. The images below show the messages you should look for.  
          Shown above: Message explaining the due date has passed. You can keep practicing, but scores will not increase after the Canvas due date has passed.
          Shown above: Message explaining all the attempts have been used. You can keep practicing, but scores will not increase once attempt limits are reached.
          `,
          answer: (
            <>
              <div className={classes.paragraph}>
                Even though you can retry the lesson quizzes as many times as you like,
                your final graded score will not update after one of the following
                happens:
                <ol>
                  <li>You reached the maximum attempt limits for the quiz.</li>
                  <li>The Canvas assignment is past due.</li>
                </ol>
                If your score isn’t changing, check the quiz’s due date in Canvas to see
                if it’s already past due, and also check if you’ve used up all your
                attempts. The images below show the messages you should look for.
              </div>
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/faq-screen-orchard-due-date-passed.png"
                  alt="Orchard screen - due date passed"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>
                      Shown above: Message explaining the due date has passed. You can
                      keep practicing, but scores will not increase after the Canvas due
                      date has passed.
                    </span>
                  </div>
                </div>
              </div>
              <div className={classes.imageContainer}>
                <img
                  style={{ width: "100%", height: "auto" }}
                  src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/faq-screen-orchard-no-attempts-remain.png"
                  alt="Orchard screen - no attempts remaining"
                />
                <div className={classes.imageNoteContainer}>
                  <div className={classes.imageNote}>
                    <span style={{ fontWeight: "bold" }}>
                      Shown above: Message explaining all the attempts have been used. You
                      can keep practicing, but scores will not increase once attempt
                      limits are reached.
                    </span>
                  </div>
                </div>
              </div>
            </>
          ),
        },
      ],
    },
    {
      id: "topic-viewing-and-accessibility",
      headingText: "Viewing and accessibility",
      headingIcon: <i className="fa-solid fa-eye"></i>,
      questions: [
        {
          tags: allCoursesTags,
          question:
            "I want to use my tablet or mobile phone to complete Digit assignments.",
          answerRaw:
            "Digit is not yet fully optimized for mobile viewing. Currently, the tool works best on screens that are at least 11.6 inches, measured diagonally. Stay tuned for further optimization! Until then, please use a Chromebook, laptop, or PC to access Digit. Reminder: Chrome is the preferred browser for Canvas and Digit.",
          answer: (
            <>
              Digit is not yet fully optimized for mobile viewing. Currently, the tool
              works best on screens that are at least 11.6 inches, measured diagonally.
              Stay tuned for further optimization! Until then, please use a Chromebook,
              laptop, or PC to access Digit.{" "}
              <span style={{ fontWeight: "bold" }}>Reminder:</span> Chrome is the
              preferred browser for Canvas and Digit.
            </>
          ),
        },
        {
          tags: allCoursesTags,
          question: "I cannot see the entire question on my screen.",
          answerRaw: `Increase your screen resolution to at least 1366 x 768 (Chromebook size) for optimal viewing. If issues persist, try zooming out to view the full question.`,
          answer: (
            <>
              Increase your screen resolution to at least 1366 x 768 (Chromebook size) for
              optimal viewing. If issues persist, try zooming out to view the full
              question.
            </>
          ),
        },
        {
          tags: allCoursesTags,
          question: "The image for a question is too small to see the details I need.",
          answerRaw: `Use one of the platform's two enlarge-image tools when either is available. The first tool is a plus sign (+) that appears in the upper-right corner of images, and the second is a link icon that appears in the center of  images. Which tool you see, and whether you see either at all, depends on the type of learning activity you are in.`,
          answer: (
            <>
              Use one of the platform's two enlarge-image tools when either is available.
              The first tool is a plus sign (+) that appears in the upper-right corner of
              images, and the second is a link icon (<i className="fa-solid fa-link"></i>)
              that appears in the center of images. Which tool you see, and whether you
              see either at all, depends on the type of learning activity you are in.
            </>
          ),
        },
        {
          tags: allCoursesTagsGraded,
          question:
            "I do not see learning support materials/Additional Learning activities.",
          answerRaw: `
          There are a few reasons you may not see them yet, and several ways to access them depending on your progress:
          Automatic Unlocking Based on Performance
After you complete a Practice activity, the system checks your performance. If your score suggests you could benefit from additional support, the system will unlock:
A Worked Example Video with explanations of correct answers (when available)
An Additional Practice activity (when available)
These support materials appear in the Activity list on the right side of the Digit screen. (Note: This list can be collapsed or expanded using the black button above it.)
"Explore More Learning" Pop-Up
When you finish a Practice activity, you may see an “Explore More Learning” button in the pop-up that appears. Clicking this will also unlock additional support materials.
Tip: Use the “Show Solution” option before clicking “Explore More Learning” or leaving the results screen.
‘Unlock additional learning?’ Pop-Up
You may see the popup below towards the bottom of some of your activities. Clicking ‘Yes’ will also unlock additional support materials.
Toggle to Show All Support Materials
If none of the options above appear, support materials will become available once you complete all required activities in the learning map. At that point, a “Completed” icon and a “Show All Learning Activities” toggle will appear at the bottom of the right-hand menu. Turn this on to display all learning support materials.
We encourage you to “Unlock additional learning”! 
Here are a few reminders:
For Practice activities that have scores, the system will automatically “unlock” available Additional Learning content when your score is below 80% regardless of whether or not you click on the “Explore More Learning” button in the pop-up.
 In the learning activities pane, Additional Learning content can be collapsed/hidden and expanded/shown using the plus (+) and minus (-) signs that appear next to the primary learning path content for which it is available.
Regardless of scores, everyone can access the secondary path/Additional Learning activities after completing all of the activities on the primary learning path using the “Show All Learning Activities” toggle.

          `,
          answer: (
            <>
              There are a few reasons you may not see them yet, and several ways to access
              them depending on your progress:
              <ol style={{ marginTop: "1em", marginBottom: "0px" }}>
                <li>
                  <div style={{ fontWeight: "bold" }}>
                    Automatic Unlocking Based on Performance
                  </div>
                  <div className={classes.paragraph}>
                    After you complete a{" "}
                    <span style={{ fontWeight: "bold" }}>Practice activity</span>, the
                    system checks your performance. If your score suggests you could
                    benefit from additional support, the system will unlock:
                    <ul>
                      <li>
                        A <span style={{ fontWeight: "bold" }}>Worked Example Video</span>{" "}
                        with explanations of correct answers (when available)
                      </li>
                      <li>
                        An{" "}
                        <span style={{ fontWeight: "bold" }}>
                          Additional Practice activity
                        </span>{" "}
                        (when available)
                      </li>
                    </ul>
                    These support materials appear in the{" "}
                    <span style={{ fontWeight: "bold" }}>Activity list</span> on the right
                    side of the Digit screen. (Note: This list can be collapsed or
                    expanded using the black button above it.)
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        width: "100%",
                      }}>
                      <img
                        src="https://macs-assets.s3.us-east-1.amazonaws.com/orchard-expanding-additional-learning.gif"
                        alt="expanding additional learning in orchard"
                      />
                    </div>
                  </div>
                </li>
                <li>
                  <div style={{ fontWeight: "bold" }}>"Explore More Learning" Pop-Up</div>
                  <div className={classes.paragraph}>
                    When you finish a Practice activity, you may see an “Explore More
                    Learning” button in the pop-up that appears. Clicking this will also
                    unlock additional support materials.
                  </div>
                  <div style={{ fontStyle: "italic" }}>
                    <span style={{ fontWeight: "bold" }}>Tip:</span> Use the “Show
                    Solution” option before clicking “Explore More Learning” or leaving
                    the results screen.
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        width: "100%",
                      }}>
                      <img
                        src="https://macs-assets.s3.us-east-1.amazonaws.com/bio-explore-more-learning.png"
                        alt="explore more learning button"
                      />
                    </div>
                  </div>
                </li>
                <li>
                  <div style={{ fontWeight: "bold" }}>
                    ‘Unlock additional learning?’ Pop-Up
                  </div>
                  <div className={classes.paragraph}>
                    You may see the popup below towards the bottom of some of your
                    activities. Clicking ‘Yes’ will also unlock additional support
                    materials.
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        width: "100%",
                      }}>
                      <img
                        src="https://macs-assets.s3.us-east-1.amazonaws.com/bio-unlock-additional-learning.png"
                        alt="unlock additional learning popup"
                      />
                    </div>
                  </div>
                </li>
                <li>
                  <div style={{ fontWeight: "bold" }}>
                    Toggle to Show All Support Materials
                  </div>
                  <div className={classes.paragraph}>
                    If none of the options above appear, support materials will become
                    available once you complete all required activities in the learning
                    map. At that point, a “Completed” icon and a “Show All Learning
                    Activities” toggle will appear at the bottom of the right-hand menu.
                    Turn this on to display all learning support materials.
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        width: "100%",
                      }}>
                      <img
                        src="https://macs-assets.s3.us-east-1.amazonaws.com/show-all-learning-activities-toggle.png"
                        alt="show all learning activities toggle"
                      />
                    </div>
                  </div>
                </li>
              </ol>
              <div>
                <span style={{ fontWeight: "bold" }}>
                  We encourage you to “Unlock additional learning”!{" "}
                </span>
              </div>
              <div className={classes.paragraph}>
                Here are a few reminders:
                <ul style={{ marginTop: "0px", marginBottom: "0px" }}>
                  <li>
                    For Practice activities that have scores, the system will
                    automatically “unlock” available Additional Learning content when your
                    score is below 80% regardless of whether or not you click on the
                    “Explore More Learning” button in the pop-up.
                  </li>
                  <li>
                    In the learning activities pane, Additional Learning content can be
                    collapsed/hidden and expanded/shown using the plus (+) and minus (-)
                    signs that appear next to the primary learning path content for which
                    it is available.
                  </li>
                  <li>
                    Regardless of scores, everyone can access the secondary
                    path/Additional Learning activities after completing all of the
                    activities on the primary learning path using the “Show All Learning
                    Activities” toggle.
                  </li>
                </ul>
              </div>
            </>
          ),
        },
      ],
    },
  ];
  const fullContentRef = useRef(fullContent);
  fullContentRef.current = fullContent;

  const filteredContent = useMemo<ISection[]>(() => {
    const filterContent = (
      contentToFilter: ISection[],
      searchString: string,
      selectedTags: CourseTag[]
    ): ISection[] => {
      const lowerSearchString = searchString.toLowerCase();
      const filtered: ISection[] = [];

      contentToFilter.forEach((section) => {
        const isHeadingMatch =
          lowerSearchString &&
          section.headingText.toLowerCase().includes(lowerSearchString);

        const filteredQuestions = section.questions.filter(
          (question) =>
            (question.question.toLowerCase().includes(lowerSearchString) ||
              question.answerRaw.toLowerCase().includes(lowerSearchString)) &&
            (selectedTags.length === 0 ||
              selectedTags.some((tag) => question.tags.includes(tag)))
        );

        if (isHeadingMatch && filteredQuestions.length < 1) {
          filtered.push(section);
        } else if (filteredQuestions.length > 0) {
          filtered.push({ ...section, questions: filteredQuestions });
        }
      });

      return filtered;
    };

    return filterContent(fullContentRef.current, searchText.trim(), selectedTags);
  }, [searchText, selectedTags]);

  const selectCourse = useCallback(
    (courseTag: CourseTag) => {
      setSelectedCourse(courseTag);
      history.push(`/faq/${courseTag}`, { focusFaqContent: true });
    },
    [history, setSelectedCourse]
  );

  const clearFilters = useCallback(() => {
    setSearchText("");
    setSelectedTags([]);
    setSelectedCourse(null);
  }, [setSelectedCourse]);

  useEffect(() => {
    if (singleCourseTag && selectedCourse) {
      if (allCoursesTags.includes(selectedCourse as CourseTag)) {
        setSelectedTags([selectedCourse] as CourseTag[]);
      }
    } else if (!singleCourseTag && selectedCourse) {
      if (allCoursesTags.includes(selectedCourse as CourseTag)) {
        selectCourse(selectedCourse as CourseTag);
      }
    }
  }, [selectedCourse, singleCourseTag, setSelectedCourse, clearFilters, selectCourse]);

  const showCourseSelect = useMemo(() => {
    if (singleCourseTag) {
      // invalid tag is selected, clear it and return to select screen
      if (selectedCourse && !allCoursesTags.includes(selectedCourse as CourseTag)) {
        clearFilters();
        return true;
      } else {
      }
    } else {
      if (selectedCourse && !allCoursesTags.includes(selectedCourse as CourseTag)) {
        clearFilters();
      }
      if (!selectedCourse && !searchText) {
        return true;
      }
    }
    return false;
  }, [clearFilters, searchText, selectedCourse, singleCourseTag]);

  const faqCourseTableScrollRef = useRef<HTMLUListElement>(null);
  const faqMainColumnRef = useRef<HTMLDivElement>(null);
  const [showCourseTableFade, setShowCourseTableFade] = useState(false);
  const [selectedCourseCategory, setSelectedCourseCategory] =
    useState<FaqCourseCategoryId>("all");

  useEffect(() => {
    if (!singleCourseTag || search || showCourseSelect) return;
    if (!location.state?.focusFaqContent) return;
    if (filteredContent.length === 0) return;

    const frame = requestAnimationFrame(() => {
      const firstAccordion = faqMainColumnRef.current?.querySelector<HTMLElement>(
        ".MuiAccordionSummary-root"
      );
      firstAccordion?.focus();

      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: undefined,
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [
    singleCourseTag,
    search,
    showCourseSelect,
    location.pathname,
    location.search,
    location.hash,
    location.state?.focusFaqContent,
    filteredContent.length,
    history,
  ]);

  const filteredFaqCourseTableRows = useMemo(
    () =>
      faqCourseTableRows.filter(([tag]) =>
        faqCourseMatchesCategory(tag, selectedCourseCategory)
      ),
    [selectedCourseCategory]
  );

  const updateCourseTableFade = useCallback(() => {
    const el = faqCourseTableScrollRef.current;
    if (!el) {
      setShowCourseTableFade(false);
      return;
    }
    const canScroll = el.scrollHeight > el.clientHeight + 1;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    setShowCourseTableFade(canScroll && !atBottom);
  }, []);

  useEffect(() => {
    if (!showCourseSelect || search) return;
    updateCourseTableFade();
    const el = faqCourseTableScrollRef.current;
    if (!el) return;
    const resizeObserver = new ResizeObserver(updateCourseTableFade);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [
    showCourseSelect,
    search,
    updateCourseTableFade,
    filteredFaqCourseTableRows.length,
  ]);

  useEffect(() => {
    if (!showCourseSelect || search) return;
    if (faqCourseTableScrollRef.current) {
      faqCourseTableScrollRef.current.scrollTop = 0;
    }
    updateCourseTableFade();
  }, [
    selectedCourseCategory,
    filteredFaqCourseTableRows.length,
    showCourseSelect,
    search,
    updateCourseTableFade,
  ]);

  const onSearchClear = useCallback(() => {
    setInputText("");
    setSearchText("");
  }, []);

  const searchBox = (
    <FAQSearch
      key="faq-search"
      inputText={inputText}
      onInputChange={setInputText}
      onSearch={onSearch}
      onClear={onSearchClear}
      variant={showCourseSelect && !search ? "landing" : "light"}
    />
  );

  const addCourseTagFilter = (tag: CourseTag) => {
    setSelectedTags((prevTags) => {
      if (prevTags.includes(tag)) {
        return prevTags;
      }
      return [...prevTags, tag];
    });
  };

  const removeCourseTagFilter = (tag: CourseTag) => {
    setSelectedTags((prevTags) => prevTags.filter((t) => t !== tag));
  };

  // what a nightmare
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);
  useEffect(() => {
    const observedElements: Element[] = [];
    let currentEntries: IntersectionObserverEntry[] = [];
    const observer = new IntersectionObserver(
      (entries) => {
        currentEntries = entries;
        updateActive(entries);
      },
      {
        rootMargin: "0px 0px -60% 0px",
        threshold: 0.75,
      }
    );
    function updateActive(entries: IntersectionObserverEntry[]) {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => {
          const indexA = filteredContent.findIndex(
            (section) => section.id === a.target.id
          );
          const indexB = filteredContent.findIndex(
            (section) => section.id === b.target.id
          );
          return indexB - indexA;
        });

      if (visible.length > 0) {
        setActiveTopicId(visible[0].target.id);
      }
    }
    filteredContent.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) {
        observer.observe(el);
        observedElements.push(el);
      }
    });
    const onScroll = () => {
      updateActive(currentEntries);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [filteredContent, showCourseSelect, search]);

  return (
    <>
      {showCourseSelect && !search ? (
        <Stack minHeight={0} className={classes.root} id="main">
          <Box className={classes.contentContainer}>
            <Box
              sx={{
                marginTop: "auto",
                marginBottom: "auto",
                maxWidth: "730px",
                height: "auto",
                boxSizing: "border-box",
                paddingY: 4,
              }}>
              <Stack gap="32px">
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-end"
                  sx={{ width: "100%" }}>
                  <Typography
                    variant="h1"
                    className={classes.landingHeading}
                    sx={{ margin: 0 }}>
                    FAQs
                  </Typography>
                  <Box
                    component="img"
                    src="https://macs-assets.s3.us-east-1.amazonaws.com/faq/faq-title-image.svg"
                    alt="FAQ"
                    sx={{ display: "block", flexShrink: 0 }}
                  />
                </Stack>
                {searchBox}
                <Box
                  sx={{
                    backgroundColor: "rgba(72, 72, 72, 0.4)",
                    backdropFilter: "blur(5px)",
                    padding: "24px",
                    borderRadius: "16px",
                    mb: 4,
                  }}>
                  <Box
                    component="section"
                    aria-labelledby="faq-choose-course-heading"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: "16px",
                      alignSelf: "stretch",
                    }}>
                    <Typography
                      id="faq-choose-course-heading"
                      variant="h4"
                      component="h2"
                      tabIndex={0}
                      sx={{
                        color: "#ffffff",
                        outline: "none",
                        "&:focus-visible": {
                          outline: "2px solid #007CBF",
                          outlineOffset: "2px",
                          borderRadius: "4px",
                        },
                      }}>
                      Choose your course:
                    </Typography>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      gap="8px"
                      role="group"
                      aria-labelledby="faq-choose-course-heading">
                      {faqCourseCategoryFilters.map(({ id, label, icon }) => {
                        const isSelected = selectedCourseCategory === id;
                        return (
                          <Button
                            key={id}
                            variant="outlined"
                            onClick={() => setSelectedCourseCategory(id)}
                            aria-pressed={isSelected}
                            aria-label={`${spellCategoryLabelForScreenReader(label)} (${
                              faqCourseCategoryCounts[id]
                            })`}
                            sx={{
                              ...faqCourseCategoryButtonSx,
                              ...(isSelected ? faqCourseCategoryButtonSelectedSx : {}),
                            }}>
                            {icon === "all" ? (
                              isSelected ? (
                                <FaqCourseIconAllSelected
                                  width={16}
                                  height={16}
                                  style={{ flexShrink: 0 }}
                                  aria-hidden
                                />
                              ) : (
                                <FaqCourseIconAll
                                  width={16}
                                  height={16}
                                  style={{ flexShrink: 0 }}
                                  aria-hidden
                                />
                              )
                            ) : icon === "bio" ? (
                              isSelected ? (
                                <FaqCourseIconBioSelected
                                  width={16}
                                  height={16}
                                  style={{ flexShrink: 0 }}
                                  aria-hidden
                                />
                              ) : (
                                <FaqCourseIconBio
                                  width={16}
                                  height={16}
                                  style={{ flexShrink: 0 }}
                                  aria-hidden
                                />
                              )
                            ) : icon === "chm" ? (
                              isSelected ? (
                                <FaqCourseIconChemSelected
                                  width={16}
                                  height={16}
                                  style={{ flexShrink: 0 }}
                                  aria-hidden
                                />
                              ) : (
                                <FaqCourseIconChem
                                  width={16}
                                  height={16}
                                  style={{ flexShrink: 0 }}
                                  aria-hidden
                                />
                              )
                            ) : isSelected ? (
                              <FaqCourseIconCsSelected
                                width={16}
                                height={16}
                                style={{ flexShrink: 0 }}
                                aria-hidden
                              />
                            ) : (
                              <FaqCourseIconCs
                                width={16}
                                height={16}
                                style={{ flexShrink: 0 }}
                                aria-hidden
                              />
                            )}
                            {`${label} (${faqCourseCategoryCounts[id]})`}
                          </Button>
                        );
                      })}
                    </Stack>
                    <Box sx={faqCourseTableSx}>
                      <Box sx={faqCourseTableHeaderEntrySx}>
                        <Typography
                          component="span"
                          sx={{
                            width: 92,
                            minWidth: 92,
                            flexShrink: 0,
                            boxSizing: "border-box",
                            ...faqCourseTableColumnHeaderTypography,
                          }}>
                          CODE
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            ...faqCourseTableColumnHeaderTypography,
                            ml: "12px",
                            flex: 1,
                            minWidth: 0,
                          }}>
                          COURSE NAME
                        </Typography>
                      </Box>
                      <Box sx={faqCourseTableBodySx}>
                        <Box
                          component="ul"
                          ref={faqCourseTableScrollRef}
                          onScroll={updateCourseTableFade}
                          aria-label="Courses"
                          sx={faqCourseTableScrollSx}>
                          {filteredFaqCourseTableRows.map(([tag, details], index) => {
                            const isMultiCode = tag.includes("|");
                            const courseCodes = isMultiCode
                              ? tag.split("|").filter(Boolean)
                              : [];

                            return (
                              <Box
                                component="li"
                                key={tag}
                                sx={{ display: "block", listStyle: "none" }}>
                                <Box
                                  component="button"
                                  type="button"
                                  onClick={() => selectCourse(tag)}
                                  aria-label={getCourseRowAriaLabel(tag, details)}
                                  sx={faqCourseTableRowButtonSx(
                                    index % 2 === 0 ? "#F2F2F2" : "#FFFFFF"
                                  )}>
                                  <Box sx={faqCourseTableRowContentSx(isMultiCode)}>
                                    <Box
                                      aria-hidden
                                      sx={{
                                        ...faqCourseTableDotSx,
                                        ...(isMultiCode ? { mt: "6px" } : {}),
                                      }}
                                    />
                                    {isMultiCode ? (
                                      <Box
                                        aria-hidden
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          width: 74,
                                          minWidth: 74,
                                          maxWidth: 74,
                                          flexShrink: 0,
                                          boxSizing: "border-box",
                                        }}>
                                        {courseCodes.map((code) => (
                                          <Typography
                                            key={code}
                                            component="span"
                                            className="faq-course-row-code"
                                            sx={faqCourseCodeTypography}>
                                            {code}
                                          </Typography>
                                        ))}
                                      </Box>
                                    ) : (
                                      <FAQTruncatedText
                                        text={details.subTitle}
                                        className="faq-course-row-code"
                                        aria-hidden
                                        sx={{
                                          width: 74,
                                          minWidth: 74,
                                          maxWidth: 74,
                                          flexShrink: 0,
                                          boxSizing: "border-box",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          ...faqCourseCodeTypography,
                                        }}
                                      />
                                    )}
                                    <Box sx={faqCourseTableDividerSx} aria-hidden />
                                    <Typography
                                      component="span"
                                      aria-hidden
                                      sx={{
                                        ...faqCourseNameTypography,
                                        ml: "12px",
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        pr: "28px",
                                      }}>
                                      {details.name}
                                    </Typography>
                                    <Box
                                      component="i"
                                      className="fa-regular fa-chevron-right faq-course-row-chevron"
                                      aria-hidden
                                      sx={{
                                        ...faqCourseTableRowChevronSx,
                                        ...(isMultiCode
                                          ? { top: "8px", transform: "none" }
                                          : {}),
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                        {showCourseTableFade && (
                          <Box aria-hidden sx={faqCourseTableFadeOverlaySx} />
                        )}
                      </Box>
                      <Box sx={faqCourseTableFooterEntrySx} aria-hidden />
                    </Box>
                  </Box>
                </Box>
              </Stack>
              <FAQFooter dark />
            </Box>
          </Box>
        </Stack>
      ) : (
        <PageContainer>
          <Stack direction="column">
            <Box sx={{ mb: 4 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                sx={{ mb: "16px" }}>
                <Breadcrumbs
                  separator="›"
                  aria-label="breadcrumb"
                  sx={{
                    marginBottom: 0,
                    flex: 1,
                    minWidth: 0,
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    flexWrap: "nowrap",
                    overflow: "hidden",
                    "& .MuiBreadcrumbs-separator": {
                      display: "flex",
                      alignItems: "center",
                      mx: "12px",
                      color: "#747474",
                      fontFamily: '"Font Awesome 6 Pro"',
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 900,
                      lineHeight: "normal",
                      textTransform: "uppercase",
                    },
                    "& .MuiBreadcrumbs-li": {
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                    },
                  }}>
                  <Link
                    underline="none"
                    component="button"
                    type="button"
                    onClick={() => {
                      clearFilters();
                      history.push("/faq");
                    }}
                    sx={{
                      border: "none",
                      background: "none",
                      padding: 0,
                      cursor: "pointer",
                      color: "#747474",
                      fontFamily: "Roboto",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "normal",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                      display: "inline-flex",
                      alignItems: "center",
                    }}>
                    FAQ
                  </Link>
                  {!singleCourseTag ? (
                    <Typography
                      sx={{
                        color: "#005685",
                        fontFamily: "Roboto",
                        fontSize: "16px",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "normal",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                      }}>
                      Search Results
                    </Typography>
                  ) : (
                    selectedCourse && (
                      <Typography
                        sx={{
                          color: "#005685",
                          fontFamily: "Roboto",
                          fontSize: "16px",
                          fontStyle: "normal",
                          fontWeight: 400,
                          lineHeight: "normal",
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                          display: "inline-flex",
                          alignItems: "center",
                        }}>
                        Search Results
                      </Typography>
                    )
                  )}
                </Breadcrumbs>
                {singleCourseTag && selectedCourse && (
                  <FAQCourseDropdown
                    selectedCourse={selectedCourse as CourseTag}
                    courseDetails={courseDetails}
                    onSelectCourse={selectCourse}
                    alignRightToRef={faqMainColumnRef}
                  />
                )}
              </Stack>
            </Box>
            <Box sx={{ mb: "60px" }}>{searchBox}</Box>

            <Stack direction="row" width="100%" gap={isSmallScreen ? "16px" : "64px"}>
              {/* Main Content — first in DOM for keyboard order; visually on the right */}
              <Stack
                ref={faqMainColumnRef}
                component="section"
                aria-label={
                  selectedCourse
                    ? `FAQs for ${
                        courseDetails.get(selectedCourse as CourseTag)?.name ?? "course"
                      }`
                    : "FAQs"
                }
                flexGrow={1}
                minWidth={0}
                gap="40px"
                sx={{ order: 2 }}>
                {(searchText || !singleCourseTag) && (
                  <Box>
                    {searchText && (
                      <Typography
                        variant="h5"
                        component="div"
                        color="#747474"
                        fontWeight={700}
                        fontSize="20px"
                        letterSpacing="0px">
                        Search results for:{" "}
                        <Box component="span" color="#191919">
                          {searchText}
                        </Box>
                      </Typography>
                    )}

                    {!singleCourseTag && (
                      <Stack
                        direction="row"
                        flexWrap="nowrap"
                        alignItems={"center"}
                        sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          component="span"
                          fontWeight={400}
                          fontSize="18px"
                          marginRight="32px">
                          Filter:
                        </Typography>
                        <Box flexGrow={0} minWidth={0}>
                          <SimpleCarousel>
                            {Array.from(courseDetails.entries()).map(([tag, details]) => (
                              <Chip
                                label={details.name}
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addCourseTagFilter(tag);
                                }}
                                onDelete={
                                  selectedTags.includes(tag)
                                    ? (e) => {
                                        e.stopPropagation();
                                        removeCourseTagFilter(tag);
                                      }
                                    : undefined
                                }
                                sx={{
                                  borderWidth: "2px",
                                  fontWeight: 700,
                                  backgroundColor: selectedTags.includes(tag)
                                    ? "#484848"
                                    : "#FFF",
                                  color: selectedTags.includes(tag)
                                    ? "#ffffff"
                                    : "#484848",

                                  borderColor: selectedTags.includes(tag)
                                    ? "#484848"
                                    : "default",
                                  cursor: selectedTags.includes(tag)
                                    ? "default"
                                    : "pointer", // Disable pointer
                                  "& .MuiChip-deleteIcon": {
                                    color: selectedTags.includes(tag)
                                      ? "#ffffff"
                                      : "inherit",
                                    cursor: "pointer",
                                    pointerEvents: "auto", // Allow interaction even if parent has disabled pointer
                                  },
                                  "&:hover": {
                                    backgroundColor: selectedTags.includes(tag)
                                      ? "#484848 !important"
                                      : "rgba(0, 0, 0, 0.04)",
                                  },
                                }}
                              />
                            ))}
                          </SimpleCarousel>
                        </Box>
                      </Stack>
                    )}
                  </Box>
                )}
                {filteredContent.map((section) => (
                  <FAQSection
                    onHover={() => {
                      setActiveTopicId(section.id);
                    }}
                    section={section}
                    key={section.headingText}
                    singleCourseTag={singleCourseTag}
                    selectedTags={selectedTags}
                  />
                ))}
                {filteredContent.length < 1 && <FAQNoResults searchText={searchText} />}
                <Box marginBottom="136px">
                  <FAQFooter />
                </Box>
              </Stack>

              {/* Topics (Sidebar) — second in DOM; visually on the left */}
              <Stack
                component="nav"
                aria-label="FAQ topics"
                minWidth="222px"
                flexGrow={0}
                gap="24px"
                position="sticky"
                top="40px"
                alignSelf="flex-start"
                height="auto"
                sx={{ order: 1 }}>
                <Typography variant="body1" sx={{ fontSize: 20, fontWeight: "bold" }}>
                  Topics
                </Typography>
                <Stack direction="column" gap="24px">
                  {filteredContent.map((section, index) => {
                    const isActive = activeTopicId === section.id;
                    return (
                      <Typography
                        component="a"
                        href={`#${section.id}`}
                        key={index}
                        variant="body1"
                        fontSize="18px"
                        onClick={(e) => {
                          e.preventDefault();
                          history.push(`#${section.id}`);
                          setActiveTopicId(section.id);
                          const el = document.getElementById(section.id);
                          if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "start" });
                          }
                        }}
                        sx={{
                          textDecoration: "none",
                          cursor: "pointer",
                          fontWeight: isActive ? "bold" : "normal",
                          color: "#484848",
                        }}>
                        {section.headingText}
                      </Typography>
                    );
                  })}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </PageContainer>
      )}
    </>
  );
};
